import React, { useState, useRef, useEffect } from 'react';

import './CategoryTagsInput.css';

const CategoryTagsInput = ({ value = [], onChange, suggestions = [] }) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef(null);
  const highlightedRef = useRef(null);

  useEffect(() => {
    if (highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const addCategory = (category) => {
    const trimmed = category.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue('');
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const removeCategory = (categoryToRemove) => {
    onChange(value.filter((cat) => cat !== categoryToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (['Enter', ',', 'Tab'].includes(e.key)) {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
        addCategory(filteredSuggestions[highlightedIndex]);
      } else if (inputValue.trim()) {
        addCategory(inputValue.trim());
      }
    } else if (e.key === 'Escape') {
      if (showSuggestions) {
        e.stopPropagation();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    } else if (e.key === 'Backspace' && !inputValue) {
      removeCategory(value[value.length - 1]);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 100);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);

    if (val.trim().length > 0) {
      const filtered = suggestions
        .filter(
          (s) =>
            s.toLowerCase().includes(val.toLowerCase()) && !value.includes(s)
        )
        .slice(0, 8);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setHighlightedIndex(-1);
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
  };

  return (
    <div
      className='category-tags-input-container'
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((category, index) => (
        <span key={index} className='category-tags-input-category-tag'>
          {category}
          <button
            type='button'
            className='category-tags-input-remove-tag-button'
            onClick={() => removeCategory(category)}
          >
            x
          </button>
        </span>
      ))}

      <div className='category-tags-input-category-input-wrapper'>
        <input
          ref={inputRef}
          type='text'
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder='Type and press comma, tab, or Enter...'
          className='category-tags-input-category-tag-input'
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <ul className='category-tags-input-category-suggestions-list'>
            {filteredSuggestions.map((s, i) => (
              <li
                key={i}
                className={i === highlightedIndex ? 'highlighted' : ''}
                onMouseDown={() => addCategory(s)}
                ref={i === highlightedIndex ? highlightedRef : null}
              >
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CategoryTagsInput;
